import type { UseFormReturnType } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"

import { findBusinessPartnerRepositoryCompanyPrefillQueryOptions } from "~/gen/api/hooks/useFindBusinessPartnerRepositoryCompanyPrefill.ts"
import type { BusinessPartnerRepositoryPrefillResponseDto } from "~/gen/api/types/BusinessPartnerRepositoryPrefillResponseDto.ts"
import type { BusinessPartnerRepositorySuggestionResponseDto } from "~/gen/api/types/BusinessPartnerRepositorySuggestionResponseDto.ts"
import { organizationRequestDtoCountryEnum } from "~/gen/api/types/OrganizationRequestDto.ts"
import type { FormFieldConfig } from "~/pages/Admin/components/form.types"
import { registrationIdStringFromRow } from "~/utils/registration-id-string-from-row"
import { showAxiosError } from "~/utils/showAxiosError"

import {
  toBusinessPartnerRepositoryPrefillSelectionFromPrefill,
  toBusinessPartnerRepositoryPrefillSelectionFromSuggestion,
} from "./utils"

type UsePrefillParams = {
  form: UseFormReturnType<Record<string, unknown>>
  onBusinessPartnerRepositorySelected?: FormFieldConfig["onBusinessPartnerRepositorySelected"]
  requesterOrganizationId?: string
}

const resetCanAnswerOnBehalfWhenOwnOrganizationSelected = (
  form: UseFormReturnType<Record<string, unknown>>,
  repositoryRecordId: string,
  requesterOrganizationId: string | undefined
): void => {
  if (
    requesterOrganizationId !== undefined &&
    repositoryRecordId === requesterOrganizationId
  ) {
    form.setFieldValue("canAnswerOnBehalf", false)
  }
}

type PrefillOrganizationResult =
  | {
      readonly success: true
      readonly organization: BusinessPartnerRepositoryPrefillResponseDto
    }
  | { readonly success: false }

/** Resets every organization subfield written by directory prefill (not `organization.name`). */
export const resetOrganizationFieldsAfterRepositoryDirectoryPrefillClears = (
  form: UseFormReturnType<Record<string, unknown>>
): void => {
  form.setFieldValue("organization.street", "")
  form.clearFieldError("organization.street")
  form.setFieldValue("organization.city", "")
  form.clearFieldError("organization.city")
  form.setFieldValue("organization.postalCode", "")
  form.clearFieldError("organization.postalCode")
  form.setFieldValue(
    "organization.country",
    organizationRequestDtoCountryEnum.FR
  )
  form.clearFieldError("organization.country")
  form.setFieldValue("organization.registrationId", [])
  form.clearFieldError("organization.registrationId")
  form.setFieldValue("organization.businessRelationship.isActive", true)
  form.clearFieldError("organization.businessRelationship.isActive")
  form.setFieldValue("organization.businessRelationship.supplierCode", "")
  form.clearFieldError("organization.businessRelationship.supplierCode")
  form.setFieldValue("organization.businessRelationship.type", null)
  form.clearFieldError("organization.businessRelationship.type")
  form.setFieldValue("organization.businessRelationship.tagNames", [])
  form.clearFieldError("organization.businessRelationship.tagNames")
}

export const usePrefill = ({
  form,
  onBusinessPartnerRepositorySelected,
  requesterOrganizationId,
}: UsePrefillParams) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation("traceability", {
    keyPrefix: "contactModal.businessPartnerRepository",
  })

  /** Company name last applied from a successful directory prefill; used to detect manual edits. */
  const linkedRepositoryCompanyNameRef = useRef<string | null>(null)

  /**
   * Bumped on each directory selection so an older in-flight prefill cannot
   * overwrite form state or error handling after a newer pick.
   */
  const prefillGenerationRef = useRef(0)

  const prefillOrganization = useCallback(
    async (
      repositoryRecordId: string,
      selectionGeneration: number
    ): Promise<PrefillOrganizationResult> => {
      try {
        const response = await queryClient.fetchQuery(
          findBusinessPartnerRepositoryCompanyPrefillQueryOptions(
            repositoryRecordId
          )
        )
        if (selectionGeneration !== prefillGenerationRef.current) {
          return { success: false }
        }
        const organization = response.data

        form.setFieldValue("organization.name", organization.name)
        form.setFieldValue("organization.street", organization.street ?? "")
        form.setFieldValue("organization.city", organization.city ?? "")
        form.setFieldValue(
          "organization.postalCode",
          organization.postalCode ?? ""
        )
        form.setFieldValue("organization.country", organization.country ?? null)
        form.setFieldValue(
          "organization.registrationId",
          organization.registrationIds.map((row) => ({
            type: row.type,
            registrationId: registrationIdStringFromRow(row),
          }))
        )
        const businessRelationship = organization.businessRelationship
        if (businessRelationship) {
          form.setFieldValue(
            "organization.businessRelationship.isActive",
            businessRelationship.isActive
          )
          form.setFieldValue(
            "organization.businessRelationship.supplierCode",
            businessRelationship.supplierCode ?? ""
          )
          form.setFieldValue(
            "organization.businessRelationship.type",
            businessRelationship.type
          )
          form.setFieldValue(
            "organization.businessRelationship.tagNames",
            businessRelationship.tagNames ?? []
          )
        } else {
          form.setFieldValue("organization.businessRelationship.isActive", true)
          form.setFieldValue(
            "organization.businessRelationship.supplierCode",
            ""
          )
          form.setFieldValue("organization.businessRelationship.type", null)
          form.setFieldValue("organization.businessRelationship.tagNames", [])
        }
        return {
          success: true,
          organization,
        }
      } catch (error: unknown) {
        if (selectionGeneration !== prefillGenerationRef.current) {
          return { success: false }
        }
        showAxiosError({
          error: error instanceof Error ? error : new Error(String(error)),
          showNotification: true,
          notificationTitle: t("prefillLoadErrorTitle"),
          defaultMessage: t("prefillLoadError"),
        })
        return { success: false }
      }
    },
    [form, queryClient, t]
  )

  const onOptionSubmit = useCallback(
    async (suggestion: BusinessPartnerRepositorySuggestionResponseDto) => {
      const repositoryRecordId = suggestion.repositoryRecordId
      const selectionGeneration = ++prefillGenerationRef.current
      const prefillResult = await prefillOrganization(
        repositoryRecordId,
        selectionGeneration
      )
      if (selectionGeneration !== prefillGenerationRef.current) {
        return
      }
      if (prefillResult.success) {
        linkedRepositoryCompanyNameRef.current = prefillResult.organization.name
        resetCanAnswerOnBehalfWhenOwnOrganizationSelected(
          form,
          prefillResult.organization.repositoryRecordId,
          requesterOrganizationId
        )
        onBusinessPartnerRepositorySelected?.(
          toBusinessPartnerRepositoryPrefillSelectionFromPrefill(
            prefillResult.organization
          )
        )
        return
      }

      linkedRepositoryCompanyNameRef.current = null
      resetCanAnswerOnBehalfWhenOwnOrganizationSelected(
        form,
        repositoryRecordId,
        requesterOrganizationId
      )
      onBusinessPartnerRepositorySelected?.(
        toBusinessPartnerRepositoryPrefillSelectionFromSuggestion(suggestion)
      )
    },
    [
      form,
      onBusinessPartnerRepositorySelected,
      prefillOrganization,
      requesterOrganizationId,
    ]
  )

  const abortDirectoryPrefillAndResetOrganizationFields = useCallback(() => {
    prefillGenerationRef.current += 1
    resetOrganizationFieldsAfterRepositoryDirectoryPrefillClears(form)
  }, [form])

  return {
    onOptionSubmit,
    linkedRepositoryCompanyNameRef,
    abortDirectoryPrefillAndResetOrganizationFields,
  }
}
